/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button, Typography, color, spacing } from '@uniquegood/realworld-studio-design';
import { useMemo, useState } from 'react';
import { WEBVIEW_AI_GUIDE_MARKDOWN, WEBVIEW_AI_PROMPT } from '../constants/webviewAiGuide';

const GUIDE_FILE_NAME = 'realworld-webview-ai-guide.md';

interface Props {
  webviewCode?: string;
}

const WebviewAiGuidePanel = ({ webviewCode = '' }: Props) => {
  const [statusMessage, setStatusMessage] = useState('');

  const shouldShowSubmitActionWarning = useMemo(() => {
    const hasCode = webviewCode.trim().length > 0;
    const hasSubmitAction = /(?:RealWorld\.)?submitAction\s*\(/.test(webviewCode);

    return hasCode && !hasSubmitAction;
  }, [webviewCode]);

  async function handleCopyPrompt() {
    try {
      await copyText(WEBVIEW_AI_PROMPT);
      setStatusMessage('AI 제작 프롬프트를 복사했어요.');
    } catch {
      setStatusMessage('복사에 실패했어요. 가이드를 다운로드해 주세요.');
    }
  }

  function handleDownloadGuide() {
    downloadTextFile(WEBVIEW_AI_GUIDE_MARKDOWN, GUIDE_FILE_NAME);
    setStatusMessage('웹뷰 제작 가이드 다운로드를 시작했어요.');
  }

  return (
    <section css={panelStyle} aria-label="AI 웹뷰 제작 가이드">
      <div css={headerRowStyle}>
        <div css={textColumnStyle}>
          <Typography type="button">AI 웹뷰 제작 가이드</Typography>
          <Typography type="caption" textColor="subdued" cssStyle={descriptionStyle}>
            생성형 AI에게 보낼 프롬프트와 Studio 리액션 설정 예시를 제공합니다.
          </Typography>
        </div>
        <div css={buttonRowStyle}>
          <Button type="plain" size="small" icon="copy_solid" onClick={handleCopyPrompt}>
            프롬프트 복사
          </Button>
          <Button type="plain" size="small" icon="download_solid" onClick={handleDownloadGuide}>
            가이드 다운로드
          </Button>
        </div>
      </div>
      <ul css={checklistStyle}>
        <li>조건 없음 리액션은 웹뷰에서 `submitAction(&apos;&apos;)`을 호출해야 실행돼요.</li>
        <li>정답/오답 분기는 `submitAction(&apos;success&apos;)`처럼 값을 보내고 조건에 맞춰요.</li>
        <li>퀘스트 완료, 미션 완료, 게임 종료는 Studio 리액션 명령으로 설정해요.</li>
      </ul>
      {shouldShowSubmitActionWarning && (
        <Typography type="caption" cssStyle={warningStyle}>
          현재 코드에서 submitAction 호출을 찾지 못했어요. 다음 화면으로 넘어가지 않을 수 있어요.
        </Typography>
      )}
      {statusMessage && (
        <Typography type="caption" textColor="subdued" cssStyle={statusStyle}>
          {statusMessage}
        </Typography>
      )}
    </section>
  );
};

export default WebviewAiGuidePanel;

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', 'true');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error('copy failed');
  }
}

function downloadTextFile(text: string, fileName: string) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

const panelStyle = css`
  padding: ${spacing.common.medium};
  background-color: ${color.surface_default_subdued};
`;

const headerRowStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.common.medium};
`;

const textColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.xsmall};
`;

const descriptionStyle = css`
  margin: 0;
`;

const buttonRowStyle = css`
  display: flex;
  flex-shrink: 0;
  gap: ${spacing.common.xsmall};
`;

const checklistStyle = css`
  margin: ${spacing.common.small} 0 0;
  padding-left: ${spacing.common.medium};
  color: ${color.text_subdued};
  font-size: 12px;
  line-height: 18px;
`;

const warningStyle = css`
  display: block;
  margin-top: ${spacing.common.small};
  color: ${color.text_critical};
`;

const statusStyle = css`
  display: block;
  margin-top: ${spacing.common.small};
`;
